import { isBadgeReleased } from 'features/catalog/badge-release';

type AutoClaimProfile = {
  submittedBadgeIDs: readonly string[];
  email: string;
};

/** Ids forced on by contribution-count thresholds (Badger / Badge Baron). */
export const SYSTEM_AWARD_BADGE_IDS = new Set(['uwpa', '65ry']);

/**
 * Resolves sheet ownership + auto-claims + system awards.
 *
 * Order matters for cycles like Olympics Winner (`ibdm`) → Woodloch 2024 (`wl24`)
 * which also *requires* `wl24`: auto-claims must finish before `restrictedRules` can strip.
 *
 * Sheet / paste ownership is sacred for `restrictedRules`: if a badge is already on
 * in the input map, tenure/requires gates must not strip it (manual grants, exceptions).
 * Auto-claimed badges that fail Requires can still be force-off'd.
 */
export function getAutoUpdatedMap(
  enabledBadgesMap: Map<string, boolean>,
  badgesMap: Map<
    string,
    {
      id: string;
      autoClaimRules?: { ids: string[]; count: number };
      banSelfClaim?: boolean;
      restrictedRules?: { ids: string[]; count: number };
      name: string;
    }
  >,
  selectedProfile: AutoClaimProfile
): Map<string, boolean> {
  // Input-owned IDs (sheet paste / edit baseline) — never strip via restrictedRules.
  const inputOwnedIds = new Set<string>();
  for (const [id, on] of enabledBadgesMap) {
    if (on) inputOwnedIds.add(id);
  }

  const newEnabledBadgesMap = new Map(enabledBadgesMap);
  const submittedBadgesCount = selectedProfile.submittedBadgeIDs.filter((id) => isBadgeReleased(id)).length;

  for (const badge of badgesMap.values()) {
    if (!newEnabledBadgesMap.has(badge.id)) newEnabledBadgesMap.set(badge.id, false);
  }

  const applyAutoClaims = (): void => {
    let changed = true;
    while (changed) {
      changed = false;
      for (const badge of badgesMap.values()) {
        const { id, autoClaimRules } = badge;
        if (!autoClaimRules || !isBadgeReleased(id) || newEnabledBadgesMap.get(id)) continue;
        const met = autoClaimRules.ids.filter((depId) => newEnabledBadgesMap.get(depId)).length >= autoClaimRules.count;
        if (!met) continue;
        newEnabledBadgesMap.set(id, true);
        changed = true;
      }
    }
  };

  // 1) Cascade auto-claims from sheet ownership (and each other) before any force-off.
  applyAutoClaims();

  // 2) Contribution-count awards.
  if (submittedBadgesCount >= 10) {
    newEnabledBadgesMap.set('uwpa', true);
  } else {
    newEnabledBadgesMap.set('uwpa', false);
  }

  if (submittedBadgesCount >= 1) {
    newEnabledBadgesMap.set('65ry', true);
  } else {
    newEnabledBadgesMap.set('65ry', false);
  }

  // 3) Cascades from system awards (e.g. Badger territory unlocks).
  applyAutoClaims();

  // 4) Force-off only after cascades — so Requires can see auto-claimed deps
  //    (Olympics Winner keeps wl24 granted from itself, then passes Requires: wl24).
  //    Skip system awards: their on/off is owned by the count blocks above
  //    (e.g. Badger must not be stripped for lacking Badge Baron).
  //    `banSelfClaim` + `autoClaimRules` milestones (Always Down / Explorer / …) MUST
  //    drop when deps fall below count — including badges already on the profile.
  //    `restrictedRules` only strip auto-claimed (non-input) badges — paste ownership wins.
  let forceOffChanged = true;
  while (forceOffChanged) {
    forceOffChanged = false;
    for (const badge of badgesMap.values()) {
      const { id, autoClaimRules, banSelfClaim, restrictedRules } = badge;
      if (SYSTEM_AWARD_BADGE_IDS.has(id)) continue;
      if (!newEnabledBadgesMap.get(id)) continue;

      if (banSelfClaim && autoClaimRules && autoClaimRules.ids.length > 0) {
        const met =
          isBadgeReleased(id) &&
          autoClaimRules.ids.filter((depId) => newEnabledBadgesMap.get(depId)).length >= autoClaimRules.count;
        if (!met) {
          newEnabledBadgesMap.set(id, false);
          forceOffChanged = true;
          continue;
        }
      }

      if (
        restrictedRules &&
        !inputOwnedIds.has(id) &&
        restrictedRules.ids.filter((depId) => newEnabledBadgesMap.get(depId)).length < restrictedRules.count
      ) {
        newEnabledBadgesMap.set(id, false);
        forceOffChanged = true;
      }
    }
  }

  return newEnabledBadgesMap;
}

/** Contribution-count awards — feed grant when the Nth contributed badge was catalogued. */
export const CONTRIBUTION_AWARD_THRESHOLDS: Readonly<Record<string, number>> = {
  '65ry': 1, // Badger
  'uwpa': 10, // Badge Baron
};
